export interface CrmContactAiSuggestion {
  title: string;
  action: string;
  reasoning: string;
}

export type CrmContactAiSuggestionStatus = "accepted" | "dismissed";

interface CrmContactAiSuggestionsCardProps {
  suggestions: CrmContactAiSuggestion[];
  statuses: Record<string, CrmContactAiSuggestionStatus>;
  onDecision: (suggestion: CrmContactAiSuggestion, index: number, action: CrmContactAiSuggestionStatus) => void;
}

export function CrmContactAiSuggestionsCard({
  suggestions,
  statuses,
  onDecision,
}: CrmContactAiSuggestionsCardProps) {
  const visibleSuggestions = suggestions.filter((suggestion) => statuses[suggestion.title] !== "dismissed");
  if (visibleSuggestions.length === 0) return null;

  return (
    <div className="space-y-2 rounded-md bg-[var(--color-background)] px-3 py-3 text-xs text-[var(--color-foreground)]">
      <p className="font-medium text-[var(--color-ai)]">AI Suggestions</p>
      <div className="grid gap-2">
        {visibleSuggestions.map((suggestion, index) => {
          const suggestionStatus = statuses[suggestion.title];
          return (
            <div
              key={suggestion.title}
              className="rounded-md border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] px-3 py-2"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-medium text-[var(--color-foreground)]">{suggestion.title}</p>
                {suggestionStatus === "accepted" ? (
                  <span className="rounded-full border border-[var(--color-success)] bg-[var(--color-success)]/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-[var(--color-success)]">
                    Accepted
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-[var(--color-ai)]">{suggestion.action}</p>
              <p className="mt-1 text-[var(--color-muted-foreground)]">{suggestion.reasoning}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => { onDecision(suggestion, index, "accepted"); }}
                  disabled={suggestionStatus === "accepted"}
                  className="rounded-md border border-[var(--color-success)]/30 px-2.5 py-1 text-[11px] text-[var(--color-success)] transition-colors hover:bg-[var(--color-success)]/10 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => { onDecision(suggestion, index, "dismissed"); }}
                  className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-[var(--color-muted)]"
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function parseCrmContactAiSuggestions(content: string): CrmContactAiSuggestion[] {
  const start = content.indexOf("[");
  const end = content.lastIndexOf("]");
  if (start === -1 || end === -1 || end <= start) {
    return [];
  }

  try {
    const parsed = JSON.parse(content.slice(start, end + 1)) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((entry) => {
        if (!entry || typeof entry !== "object") {
          return null;
        }
        const record = entry as Record<string, unknown>;
        const title = typeof record.title === "string"
          ? record.title.trim()
          : "";
        const action = typeof record.action === "string"
          ? record.action.trim()
          : "";
        const reasoning = typeof record.reasoning === "string"
          ? record.reasoning.trim()
          : "";
        if (!title || !action || !reasoning) {
          return null;
        }
        return { title, action, reasoning };
      })
      .filter((entry): entry is CrmContactAiSuggestion => Boolean(entry))
      .slice(0, 3);
  } catch {
    return [];
  }
}
